import QRCode from 'qrcode';
import store from "../../../store";
import {CabinetsSet} from "./sqftCabinetsDataMapper";
import SqftJsonApi from "../api/sqftJsonApi";
import {setAPIErrorMessage, setSqftCabinetsGlb} from "../../../actions/sqftFunctions";
import {setAbortController} from "../../../actions/sqftMenuFunctions";
import {groupUrlsByVersion, similarityByWordsExtended} from "./dataHelper";
import {getCurrentLayout} from "../../appLayout";
import sqftConfig from "../components/canvas/store/sqftConfig";
import {arePointsDifferent} from "./coordinatesHelper";
import {ALLOWED_STYLE_COLOR, isDevMode, SQFT_AR3D_GLB_VIEWER} from "../constants";
import {hardCodeIsHomeDepot, hardCodeIsRenuit} from "../../../misc/fabricatorHardcode";

export const generateQRCode = async (url) => {
  // Generate the QR code data as a string
  const qrData = await QRCode.toDataURL(url);

  // Create a canvas element to draw the QR code image on
  const canvas = document.createElement("canvas");
  canvas.width = 300;
  canvas.height = 300;
  const ctx = canvas.getContext("2d");

  // Load the QR code image data into an Image object
  const img = new Image();

  return new Promise((resolve, reject) => {
    img.onload = () => {
      // Draw the QR code image onto the canvas after it loads
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      // Resolve the promise with the canvas image data URL
      resolve(canvas.toDataURL());
    };

    img.onerror = reject; // Reject if there is an error loading the image
    img.src = qrData; // Start loading the QR code image
  });
}

export const getThirdPartyAR3DMetaData = (dbFeature, thirdPartyAR3DUrl) => {
  if (dbFeature && dbFeature.thirdPartyAR3DMetaData && thirdPartyAR3DUrl) {
    const metadataString = dbFeature.thirdPartyAR3DMetaData[thirdPartyAR3DUrl];

    // Check if the metadata string is present
    if (metadataString) {
      try {
        // Parse the metadata string as JSON
        return JSON.parse(metadataString);
      } catch (error) {
        // Handle JSON parsing error
        console.log("JSON parsing error:", error.message);
        console.log("JSON metadataString", metadataString);
        console.log("dbFeature", dbFeature.sharedFeatureName);
        return null;
      }
    } else {
      console.log("dbFeature missing metadata", dbFeature.id);
    }
  }

  return null
}

export const getDepthFromAR3DMetaData = (thirdPartyAR3DMetaData) => {
  if (thirdPartyAR3DMetaData && Array.isArray(thirdPartyAR3DMetaData.Depth) && thirdPartyAR3DMetaData.Depth.length) {
    return parseFloat(thirdPartyAR3DMetaData.Depth[0]);
  }
  return null;
};

export const getWidthFromAR3DMetaData = (thirdPartyAR3DMetaData) => {
  if (thirdPartyAR3DMetaData && Array.isArray(thirdPartyAR3DMetaData.Width) && thirdPartyAR3DMetaData.Width.length) {
    return parseFloat(thirdPartyAR3DMetaData.Width[0]);
  }
  return null;
};

export const getHeightFromAR3DMetaData = (thirdPartyAR3DMetaData) => {
  if (thirdPartyAR3DMetaData && Array.isArray(thirdPartyAR3DMetaData.Height) && thirdPartyAR3DMetaData.Height.length) {
    return parseFloat(thirdPartyAR3DMetaData.Height[0]);
  }
  return null;
};

export const getSpecialShape = (thirdPartyAR3DMetaData) => {
  if (thirdPartyAR3DMetaData && Array.isArray(thirdPartyAR3DMetaData.SpecialShape) && thirdPartyAR3DMetaData.SpecialShape.length) {
    return thirdPartyAR3DMetaData.SpecialShape[0];
  }
  return null;
};

export const getCategoryFromMetadata = (thirdPartyAR3DMetaData) => {
  if (thirdPartyAR3DMetaData && Array.isArray(thirdPartyAR3DMetaData.Category) && thirdPartyAR3DMetaData.Category.length) {
    return thirdPartyAR3DMetaData.Category[0];
  }
  return null;
};

export const getDepthFromDescription = (dbFeature) => {
  if (dbFeature.description) {
    const extractDimension = (dim) => parseInt(dbFeature.description.substr(dbFeature.description.indexOf(dim) + dim.length + 1, 3), 10);
    return extractDimension('Depth');
  }
  return null;
};

export const getWidthFromDescription = (dbFeature) => {
  if (dbFeature.description) {
    const extractDimension = (dim) => parseInt(dbFeature.description.substr(dbFeature.description.indexOf(dim) + dim.length + 1, 3), 10);
    return extractDimension('Width');
  }
  return null;
};

export const getHeightFromDescription = (dbFeature) => {
  if (dbFeature.description) {
    const extractDimension = (dim) => parseInt(dbFeature.description.substr(dbFeature.description.indexOf(dim) + dim.length + 1, 3), 10);
    return extractDimension('Height');
  }
  return null;
};

export const getWidthFromDbFeature = (dbFeature, thirdPartyAR3DMetaData) => {
  let width = getWidthFromAR3DMetaData(thirdPartyAR3DMetaData);
  if (!width) {
    width = getWidthFromDescription(dbFeature);
  }
  return width
};

export const getDepthFromDbFeature = (dbFeature, thirdPartyAR3DMetaData) => {
  let depth = getDepthFromAR3DMetaData(thirdPartyAR3DMetaData);
  if (!depth) {
    depth = getDepthFromDescription(dbFeature);
  }
  return depth
};

export const getHeightFromDbFeature = (dbFeature, thirdPartyAR3DMetaData) => {
  let height = getHeightFromAR3DMetaData(thirdPartyAR3DMetaData);
  if (!height) {
    height = getHeightFromDescription(dbFeature);
  }
  return height
};

export const findGLBLink = (links) => {
  if (!Array.isArray(links)) {
    return null;
  }

  const glbLink = links.find(link => link.endsWith('.glb'));
  return glbLink || null;
};

// Function to normalize strings for comparison
export const normalizeString = (str) => {
  return str.toLowerCase().replace(/[\W_]+/g, ''); // Remove non-alphanumeric characters and convert to lowercase
}

// Function to split and normalize feature name or URL part for comparison
export const splitAndNormalize = (str) => {
  // Split by spaces and dashes, then normalize each part
  return str.split(/[\s\-]+/).map(normalizeString);
}

// Function to split and normalize feature name or URL part for comparison
export const getUrlByName = (nameSearch, thirdPartyAR3DUrls) => {
  const featureNameParts = splitAndNormalize(nameSearch);

  // 1.urlBySharedFeatureName - Enhanced Matching
  return thirdPartyAR3DUrls.find(url => {
    const urlParts = splitAndNormalize(url.toLowerCase().split('/').pop().split('.')[0]);
    return featureNameParts.every(part => urlParts.includes(part));
  });
}

// Function to get 3D URLs for a feature
export const get3dURLsForFeature = (feature) => {
  // Check if feature, feature.getFeatureName, and feature.dbFeature are defined
  if (!feature || !feature.getFeatureName || typeof feature.getFeatureName !== 'function' || !feature.dbFeature) {
    console.log('Invalid feature or feature properties');
    return null;
  }

  const featureNameParts = splitAndNormalize(feature.getFeatureName());

  // Check if thirdPartyAR3DUrls is defined and is an array
  if (!Array.isArray(feature.dbFeature.thirdPartyAR3DUrls)) {
    console.log('thirdPartyAR3DUrls is not defined or not an array. Id:', feature.dbFeature.id);
    return null;
  }

  const thirdPartyAR3DUrls = feature.dbFeature.thirdPartyAR3DUrls.filter(url => url.endsWith('.glb'));

  // Enhanced Matching
  const urlByFeatureName = thirdPartyAR3DUrls.find(url => {
    const urlParts = splitAndNormalize(url.toLowerCase().split('/').pop().split('.')[0]);
    // Check if all parts of the feature name are present in the URL segment
    return featureNameParts.every(part => urlParts.includes(part));
  });

  console.log("urlByFeatureName", urlByFeatureName);
  if (urlByFeatureName) {
    return urlByFeatureName;
  }

  return null;
};

// Function to check if a URL contains "-HE" right before the file extension
export const containsHEBeforeExtension = (url) => {
  // Regular expression to match "-HE" right before the file extension
  const pattern = /-HE\.[^/.]+$/;
  return pattern.test(url);
}

// Function to check if a URL contains "-HE" right before the file extension
export const startsFromH = (str) => {
  return str.charAt(0) === 'H';
}

// Function to check if a URL contains "-HE" right before the file extension
export const isHoodShape = (shape) => {
  if (!shape) {
    return false;
  }

  const layout = getCurrentLayout();
  const relatedParentFeature = layout.getFeatureById(shape.relatedParentFeatureId, true);
  if (!relatedParentFeature) {
    return false;
  }

  let shapeDbFeature = relatedParentFeature.getFeatureRelatedFeatures?.()?.find((relatedFeature) => relatedFeature.id === shape.featureId);

  if (!shapeDbFeature) {
    const shapeFeature = shape.getFeature?.();
    if (shapeFeature?.dbFeature) {
      shapeDbFeature = shapeFeature.dbFeature;
    }
  }

  if (!shapeDbFeature) {
    return false;
  }

  const matchedUrl = getUrlMatchingByColor(shapeDbFeature, relatedParentFeature);
  const url = matchedUrl ? matchedUrl.replace(/\s+\.glb$/, '.glb') : '';
  const metaData = getThirdPartyAR3DMetaData(shapeDbFeature, url);
  const category = getCategoryFromMetadata(metaData);
  const shapeHeight = shape.getHeight?.();

  return category === "Hood" || containsHEBeforeExtension(url) || shapeHeight <= 18;
}

export const setCabinetGLBFromCabinetsSet = async(displayDoorsOnly = false, shouldDisplayFramedDoors = false) => {
  const cabinetsData = CabinetsSet.prepareCabinetsSet(displayDoorsOnly, shouldDisplayFramedDoors);
  console.log("prepared cabinetsData", cabinetsData);

  if (sqftConfig.threeD.glbViewer === SQFT_AR3D_GLB_VIEWER.singleModel) {
    const cabinetsUrl = await getUrlToCabinetGLB(cabinetsData);
    setUrlToCabinetGLB(cabinetsUrl);
  } else {
    await setCabinetsSetToCabinetGLB(cabinetsData);
  }
}

export const setCabinetsSetToCabinetGLB = async(cabinetsData) => {
  store.dispatch(setSqftCabinetsGlb(cabinetsData));
}

export const getUrlToCabinetGLB = async(cabinetsData) => {
  const state = store.getState();
  if (state.sqftState.abortRequestController) {
    state.sqftState.abortRequestController.abort();
  }

  let cabinetSet;
  try {
    store.dispatch(setAPIErrorMessage(null));
    const abortController = new AbortController();
    store.dispatch(setAbortController(abortController));
    cabinetSet = await SqftJsonApi.postCabinetSet(cabinetsData, { signal: abortController.signal });
  } catch (e) {
    if (e.name !== 'AbortError') {
      store.dispatch(setAPIErrorMessage(e));
    }
  }
  console.log("response cabinetSet from api", cabinetSet );

  return cabinetSet
}

export const setUrlToCabinetGLB = (cabinetSet) => {
  //URL needs to be here to display it in AR https://s3.amazonaws.com/qc-3dfiles/composed/ to override it check SquareFootageToolAR
  //glbUrl = "https://qc-3dfiles.s3.amazonaws.com/ar-composer/W3030.glb"
  //glbUrl = "/images/sqft-icons/3d/Chair.glb"
  //store.dispatch(setSqftCabinetsGlb("/video/3d/cabinet.glb"));
  //store.dispatch(setSqftCabinetsGlb("https://quoteremodel.com/video/sqft/cabinets/cabinet.glb"));
  //glbUrl = "/images/sqft-icons/3d/DCW2430 Summer Flame.glb"
  //glbUrl = "/images/sqft-icons/3d/hood/Hood Oven GLB/Appliances/Hood_0_0_0_30_0_0_Collection/All-Hood ZLINE 8654SNZ CB-30-25.6.glb"
  //glbUrl = "https://discoverthreejs.com/examples/assets/models/Parrot.glb"
  //glbUrl = "https://threejs.org/examples/models/gltf/AnisotropyBarnLamp.glb"
  //setSqftCabinetsGlb("https://threejs.org/examples/models/gltf/AnisotropyBarnLamp.glb");
  //store.dispatch(setSqftCabinetsGlb("https://master_new_1512.quoteremodel.com/content/Views/visualizers/images/sqft-icons/3d/cabinet.glb"));
  //store.dispatch(setSqftCabinetsGlb("https://d1mb1i73swc8dz.cloudfront.net/ar-composer/All-Bottom Cabinet-Light Italian Walnut-Bryant-30-34.5-SB.glb"));
  //store.dispatch(setSqftCabinetsGlb("https://s3.amazonaws.com/qc-3dfiles/composed/5dd7954e-ed43-4489-8260-93eeaade08c5.glb"));
  store.dispatch(setSqftCabinetsGlb(cabinetSet ? cabinetSet.s3Url : null));
}

export const isGlbModel = (url) => {
  if (!url || typeof url !== 'string') {
    return false;
  }

  // Remove query params before checking extension
  const cleanUrl = url.split('?')[0];

  if (cleanUrl.toLowerCase().endsWith('.glb')) {
    return true;
  }

  return false;
};

export function getUrlMatchingByColor(dbFeature, relatedParentFeature) {
  //console.log("relatedParentFeature.getFeatureName()", relatedParentFeature.getFeatureName());
  //console.log("relatedParentFeature.sharedFeatureName", relatedParentFeature.dbFeature.sharedFeatureName);

  if (!dbFeature || !relatedParentFeature) {
    console.error('Invalid feature or feature properties');
    return null;
  }

  if (!Array.isArray(dbFeature.thirdPartyAR3DUrls)) {
    console.error(
      'thirdPartyAR3DUrls is not defined or not an array. Id:',
      dbFeature.id
    );
    return null;
  }

  // Filter only GLB models
  const glbUrls = dbFeature.thirdPartyAR3DUrls.filter(isGlbModel);

  if (!glbUrls.length) {
    console.error('No GLB models found. Id:', dbFeature.id);
    return null;
  }

  const relatedParentFeatureName = relatedParentFeature.getFeatureName();

  // Sort URLs by version in descending order
  const sortedUrls = groupUrlsByVersion(glbUrls);

  // Try to find a URL by the shared feature name
  const urlBySharedFeatureName = getUrlByName(relatedParentFeature.dbFeature.sharedFeatureName, sortedUrls);
  if (urlBySharedFeatureName) {
    return urlBySharedFeatureName;
  }

  // Try to find a URL by the parent feature name
  const urlByFeatureName = getUrlByName(relatedParentFeatureName, sortedUrls);
  if (urlByFeatureName) {
    return urlByFeatureName;
  }

  // Try to find a URL by similarity based on the shared feature name
  const urlBySimilarity = similarityByWordsExtended(sortedUrls, relatedParentFeature.dbFeature.sharedFeatureName);
  if (urlBySimilarity) {
    return urlBySimilarity;
  }

  // Try to find a URL by similarity based on the parent feature name
  const urlBySimilarityFromFeatureName = similarityByWordsExtended(sortedUrls, relatedParentFeatureName);
  if (urlBySimilarityFromFeatureName) {
    return urlBySimilarityFromFeatureName;
  }

  // Check if URL contains the feature name
  let matchingUrl = sortedUrls.find(url => url.includes(relatedParentFeatureName));
  if (matchingUrl) {
    return matchingUrl;
  }

  // Check if URL contains the color name
  const color = relatedParentFeature.getColorName();
  if (color && color.length > 3) {
    matchingUrl = sortedUrls.find(url => url.includes(color));
    if (matchingUrl) {
      return matchingUrl;
    }
  }

  // If no specific color, fallback to checking for 'black' in URL
  matchingUrl = sortedUrls.find(url => url.toLowerCase().includes('black'));
  if (matchingUrl) {
    return matchingUrl;
  }

  // If no match was found, return the first URL as a fallback
  return sortedUrls[0] || null;
}

export function getRelatedFeatureMetadata (relatedFeature, parentFeature) {
  const url = getUrlMatchingByColor(relatedFeature.dbFeature, parentFeature);
  return getThirdPartyAR3DMetaData(relatedFeature.dbFeature, url);
}

export function getCabinetDimensions (relatedFeature, parentFeature) {
  const thirdPartyAR3DMetaData = getRelatedFeatureMetadata(relatedFeature, parentFeature);
  const width = getWidthFromDbFeature(relatedFeature.dbFeature, thirdPartyAR3DMetaData);
  const depth = getDepthFromDbFeature(relatedFeature.dbFeature, thirdPartyAR3DMetaData);
  const height = getHeightFromDbFeature(relatedFeature.dbFeature, thirdPartyAR3DMetaData);
  const diamondLength = thirdPartyAR3DMetaData && thirdPartyAR3DMetaData.DiamondLength ? parseFloat(thirdPartyAR3DMetaData.DiamondLength[0]) : 0;

  return { width: width, height: height, depth: depth, diamondLength: diamondLength, thirdPartyAR3DMetaData };
}

export function getCabinetSimilarFeatureFromAnotherColor (featuresListToSearchForNewFeature, currentShapeDbFeature) {
  const getCabinetBoxName = (str) => {
    if (str.includes('/')) {
      const parts = str.split('/');
      return parts[0].trim();
    } else {
      return str.trim();
    }
  }
  const cabinetBoxName = getCabinetBoxName(currentShapeDbFeature.sharedFeatureName);
  let newShapeDbFeature = featuresListToSearchForNewFeature.find(relatedFeature =>
    relatedFeature.sharedFeatureName === cabinetBoxName
  );

  // If no exact match found, search for an 'includes' match
  if (!newShapeDbFeature) {
    newShapeDbFeature = featuresListToSearchForNewFeature.find(relatedFeature =>
      relatedFeature.sharedFeatureName.includes(cabinetBoxName)
    );
  }

  return newShapeDbFeature
}

export function wasShapeResized(shape) {
  const contour_points = shape.getPointsCoords();
  const original_points = shape.getOriginalPointsCoords();
  const height = shape.getInitialHeight();
  const target_height = shape.getHeight();

  // Check if the cabinet is resized, framed, or a filler
  return (contour_points
    && arePointsDifferent(contour_points, original_points))
    || height !== target_height;
}

export function getShapePointsOnZeroRotation(shape) {
  const rotateDegrees = shape.rotatedDegrees;
  shape.rotate(-rotateDegrees, shape.Direction.LEFT);

  const data = {
    original_points: shape.getOriginalPointsCoords(),
    contour_points: shape.getPointsCoords(),
  }

  // Restore original rotation after fetching coordinates
  shape.rotate(rotateDegrees, shape.Direction.LEFT);

  return data
}

const norm = (s) => (s || "").toLowerCase().trim();
const ALLOWED_STYLE_COLOR_LC = Object.fromEntries(
  Object.entries(ALLOWED_STYLE_COLOR).map(([style, colors]) => [
    norm(style),
    colors.map(norm),
  ])
);

export const matchesAllowedCombo = (featureNameRaw, sharedNameRaw) => {
  const hay = `${norm(featureNameRaw)} ${norm(sharedNameRaw)}`;
  return Object.entries(ALLOWED_STYLE_COLOR_LC).some(([style, colors]) => {
    if (!hay.includes(style)) {
      return false; // not found profile
    }
    return colors.some(c => hay.includes(c)); //allowed color found
  });
}

export const isEnabledColorForRenuitAndHomeDepot = (relatedParentFeatureId) => {
  if (isDevMode() || (!hardCodeIsHomeDepot() && !hardCodeIsRenuit())) {
    return true;
  }

  const layout = getCurrentLayout();
  const feature = layout.getFeatureById(relatedParentFeatureId, true);

  if (!feature) {
    return false;
  }

  const featureName = feature.getFeatureName?.() ?? "";
  const sharedFeatureName = feature.getLowerCasedSharedFeatureName?.() ?? "";

  return matchesAllowedCombo(featureName, sharedFeatureName);
};
